import { t } from "@/i18n-js/instance";
import { usePunditUserContext } from "@/react/contexts/punditUserContext";
import type { CurrentCommunity } from "@/react/types/CurrentCommunity";

type SignUpFlowResponses = NonNullable<
  CurrentCommunity["sign_up_flow_responses"]
>;

const march2026LocaleRoot = "authentication_app.new_onboarding_flow_march_2026";
const legacyLocaleRoot =
  "authentication_app.questions.form.april_2025_questions.options";
const aiLocaleRoot = "circle_ai.onboarding.sign_up_questions";

const isLargeICP = ({ community_size }: SignUpFlowResponses): boolean =>
  [
    t(`${march2026LocaleRoot}.community_size_questions.options.over_5000`),
    t(`${aiLocaleRoot}.community_size_questions.options.over_5000`),
  ].includes(community_size ?? "");

const isMediumICP = ({
  community_size,
  annual_revenue,
  team_size,
}: SignUpFlowResponses): boolean => {
  const hasMediumCommunitySize = [
    t(`${march2026LocaleRoot}.community_size_questions.options.1000_5000`),
    t(`${march2026LocaleRoot}.community_size_questions.options.100_1000`),
    t(`${aiLocaleRoot}.community_size_questions.options.1000_5000`),
    t(`${aiLocaleRoot}.community_size_questions.options.100_1000`),
  ].includes(community_size ?? "");
  const hasMediumRevenue = [
    t(`${march2026LocaleRoot}.annual_revenue_questions.options.over_10m`),
    t(`${march2026LocaleRoot}.annual_revenue_questions.options.1m_10m`),
    t(`${aiLocaleRoot}.annual_revenue_questions.options.over_10m`),
    t(`${aiLocaleRoot}.annual_revenue_questions.options.1m_10m`),
  ].includes(annual_revenue ?? "");
  const hasTeam =
    !!team_size &&
    team_size !== t(`${march2026LocaleRoot}.team_questions.options.just_me`) &&
    team_size !== t(`${aiLocaleRoot}.team_questions.options.just_me`);

  return (hasMediumCommunitySize || hasMediumRevenue) && hasTeam;
};

const isRevenueAndGoalEligible = ({
  achieve_with_circle,
  annual_revenue,
}: SignUpFlowResponses): boolean => {
  const hasAnyRevenue = Boolean(annual_revenue);
  const hasEligibleGoal = [
    t(`${march2026LocaleRoot}.goal_questions.options.build_new_community`),
    t(`${march2026LocaleRoot}.goal_questions.options.migrate_to_circle`),
    t(`${aiLocaleRoot}.goal_questions.options.build_new_community`),
    t(`${aiLocaleRoot}.goal_questions.options.migrate_to_circle`),
  ].includes(achieve_with_circle ?? "");

  return hasAnyRevenue && hasEligibleGoal;
};

const isEligibleViaNewFlow = (responses: SignUpFlowResponses): boolean =>
  isLargeICP(responses) ||
  isMediumICP(responses) ||
  isRevenueAndGoalEligible(responses);

const isEligibleViaLegacyFlow = (responses: SignUpFlowResponses): boolean => {
  const { achieve_with_circle, customer_journey_stage } = responses;

  const hasEligibleGoal = [
    t(`${legacyLocaleRoot}.achieve_with_circle.build_new_community`),
    t(`${legacyLocaleRoot}.achieve_with_circle.migrate_to_circle`),
  ].includes(achieve_with_circle ?? "");

  const hasEligibleRevenue = [
    t(`${legacyLocaleRoot}.customer_journey_stage.over_250k`),
    t(`${legacyLocaleRoot}.customer_journey_stage.100_250k`),
  ].includes(customer_journey_stage ?? "");

  return hasEligibleGoal && hasEligibleRevenue;
};

export const isCommunityICP = (
  responses: SignUpFlowResponses | null | undefined,
  hasBusinessEmail: boolean,
): boolean => {
  if (!responses) return false;
  if (!hasBusinessEmail) return false;
  return isEligibleViaNewFlow(responses) || isEligibleViaLegacyFlow(responses);
};

export const useCommunityIsIcp = (): boolean => {
  const { currentCommunity } = usePunditUserContext();

  return isCommunityICP(
    currentCommunity?.sign_up_flow_responses,
    currentCommunity?.owner_has_business_email ?? false,
  );
};
